// The homepage's Demo section: demo.cloudish.app, told from above.
// Plenty of tools turn a prompt into an app; what the demo shows is that
// one prompt to Claude Code builds the system behind such a tool: a public
// app that takes prompts, a Claude Code sandbox (a replica of a private app)
// for each one, and a child key with capped credits for every app it ships.
//
// The prompt is the demo's own (its Fig. S-0), word for word, and the
// drawing is its Fig. S-1 redrawn in this site's style, with the parts
// numbered as the prompt names them, and its live apps drawn as the demo's
// three samples, each linking to its app. Static on purpose, with no moving
// parts: the demo itself draws the live numbers.

import { Fragment, useState, type MouseEvent } from 'react';
import { markHeight } from '../brand/marks';

export const DEMO_URL = 'https://demo.cloudish.app/';

// The prompt behind the demo, split where it names a part of the system:
// `part` numbers those sentences the way the drawing and the list under it do.
// Like the demo, only the first sentence shows until it's opened; copying it
// copies all of it either way.
const PROMPT: { text: string; part?: number; deploy?: boolean }[] = [
  { text: 'Build a system on Cloudish that turns a prompt into a live web app, built by Claude Code in a sandbox of its own.' },
  { text: 'A public app takes the prompts.', part: 1 },
  {
    text: 'A private runner app’s image runs Claude Code, locked down by a managed policy and an egress allowlist, and every prompt gets a fresh replica of it, deleted afterwards.',
    part: 2,
  },
  { text: 'For each prompt, mint a child key, fund it with 0.5 credits and hand it to the sandbox to deploy with.', part: 3 },
  { text: 'The sandbox reaches Claude through a proxy on the public app, with a token that dies with the run, never the Anthropic key.', part: 4 },
  { text: 'Read https://cloudish.ai/skill.md first.' },
  { text: 'Deploy to cloudish.ai.', deploy: true },
];

const PROMPT_TEXT = PROMPT.map((p) => p.text).join(' ');

// What each numbered part is, in the prompt's order.
const PARTS = [
  {
    title: 'A public app takes the prompts',
    body: 'An ordinary Cloudish project. It holds the system’s two secrets: its Cloudish key and an Anthropic key.',
  },
  {
    title: 'Every prompt gets its own sandbox',
    body: 'A fresh replica of a private app that runs Claude Code, locked down by a managed policy and an egress allowlist, and deleted when the run ends.',
  },
  {
    title: 'Every app gets its own capped key',
    body: 'A child key with 0.5 credits, behind a daily limit. Claude Code deploys with it, so each app pays for its own build and container, and can never spend more than it was given.',
  },
  {
    title: 'Claude, without the key',
    body: 'Claude Code reaches Claude through a proxy on the public app, with a token that dies with the run. The Anthropic key never enters a sandbox.',
  },
];

const ALT =
  'The system the prompt builds. A visitor’s prompt goes to a public app on Cloudish, which starts a private sandbox where Claude Code builds the app, reaching Claude through the public app’s model proxy, and deploys it as a live app with a key of its own. One prepaid Cloudish key pays for the public app and the sandboxes by the second, and gives every app 0.5 credits, capped.';

// ------------------------------------------------------------ drawing kit

const C30 = Math.cos(Math.PI / 6);
const points = (list: [number, number][]) => list.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

/** An isometric box whose top face is centered on (cx, cy): `s` wide and deep, `h` tall. */
function Iso({ cx, cy, s, h, tone }: { cx: number; cy: number; s: number; h: number; tone?: 'cool' | 'hot' | 'ghost' }) {
  const k = s * C30;
  const m = s / 2;
  return (
    <g className={tone ? `demo-iso demo-iso-${tone}` : 'demo-iso'}>
      <polygon className="demo-iso-l" points={points([[cx - k, cy], [cx, cy + m], [cx, cy + m + h], [cx - k, cy + h]])} />
      <polygon className="demo-iso-r" points={points([[cx, cy + m], [cx + k, cy], [cx + k, cy + h], [cx, cy + m + h]])} />
      <polygon className="demo-iso-t" points={points([[cx, cy - m], [cx + k, cy], [cx, cy + m], [cx - k, cy]])} />
    </g>
  );
}

/** A path that ends in an arrowhead: requests in amber, the model's in indigo, credits dashed.
 *  The head points along the path's last segment, from the point before its end. */
function Flow({ d, tone = 'amber' }: { d: string; tone?: 'amber' | 'indigo' | 'money' }) {
  const n = d.match(/-?\d+(\.\d+)?/g)!.map(Number);
  const [px, py, x, y] = n.slice(-4);
  const a = Math.atan2(y - py, x - px);
  const head = (t: number): [number, number] => [x - 7 * Math.cos(a + t), y - 7 * Math.sin(a + t)];
  return (
    <g>
      <path d={d} className={`demo-flow demo-flow-${tone}`} />
      <polyline points={points([head(0.45), [x, y], head(-0.45)])} className={`demo-arrow demo-arrow-${tone === 'indigo' ? 'indigo' : 'amber'}`} />
    </g>
  );
}

/** A part's number, as the prompt and the list under the drawing give it. */
function Badge({ x, y, n }: { x: number; y: number; n: number }) {
  return (
    <g className="demo-badge">
      <circle cx={x} cy={y} r={10.5} />
      <text x={x} y={y + 4} textAnchor="middle">
        {n}
      </text>
    </g>
  );
}

/** A name with a line under it, starting at (x, y); a badge, if any, goes in front. */
function Caption({ x, y, title, note, badge, hot }: { x: number; y: number; title: string; note?: string; badge?: number; hot?: boolean }) {
  const tx = badge ? x + 28 : x;
  return (
    <g>
      {badge ? <Badge x={x + 11} y={y - 5} n={badge} /> : null}
      <text x={tx} y={y} className={hot ? 'demo-t-title demo-t-hot' : 'demo-t-title'}>
        {title}
      </text>
      {note ? (
        <text x={tx} y={y + 18} className="demo-t-note">
          {note}
        </text>
      ) : null}
    </g>
  );
}

/** A visitor's browser, with a prompt in it. */
function Browser({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <rect x={x} y={y} width={160} height={92} rx={8} className="demo-card" />
      <line x1={x} y1={y + 18} x2={x + 160} y2={y + 18} className="demo-hair" />
      {[11, 19, 27].map((dx) => (
        <circle key={dx} cx={x + dx} cy={y + 9} r={2.4} className="demo-light" />
      ))}
      <text x={x + 12} y={y + 40} className="demo-t-code demo-t-hot">
        ›
      </text>
      <text x={x + 24} y={y + 40} className="demo-t-code">
        Build a live poll…
      </text>
      <text x={x + 24} y={y + 56} className="demo-t-code demo-t-hot">
        Deploy to
      </text>
      <text x={x + 24} y={y + 71} className="demo-t-code demo-t-hot">
        cloudish.ai.
      </text>
    </g>
  );
}

// The boxes a click opens: the public app (the demo itself) and the demo's
// three sample apps, each with what it is drawn on its top: a prompt box,
// a shared pixel board, a poll's tally, a three-column board. The demo replays these three, so they stay pinned
// to these launch pads (demo.cloudish.app/api/cache lists them); rebuilding
// one there can move it, and then its URL here has to follow.
const APPS = {
  public: { name: 'The demo', url: DEMO_URL },
  canvas: { name: 'Shared pixel canvas', url: 'https://rigel.cloudish.app/' },
  poll: { name: 'Live polls', url: 'https://altair.cloudish.app/' },
  board: { name: 'Shared kanban board', url: 'https://sirius.cloudish.app/' },
};
type AppKind = keyof typeof APPS;
type GlyphKind = AppKind | 'terminal';

// Pixels: 0 empty, 1 amber, 2 indigo.
const PIXELS = ['0110020', '1111222', '0112220', '0012000'];

/** What a box is, drawn flat in a `size` square: one of the apps, the
 *  public app's prompt box, or the sandbox's terminal. */
function Glyph({ kind, size }: { kind: GlyphKind; size: number }) {
  const pad = size * 0.14;
  const inner = size - 2 * pad;
  if (kind === 'public') {
    // A prompt field with a send button, and the prompts that came before.
    const field = inner * 0.3;
    const send = field * 0.7;
    return (
      <>
        <rect x={pad} y={pad} width={inner} height={field} rx={2} className="demo-px demo-px-0" />
        <rect x={pad + field * 0.3} y={pad + field * 0.4} width={inner * 0.5} height={field * 0.2} rx={1} className="demo-px demo-px-2" />
        <rect x={pad + inner - send - field * 0.15} y={pad + (field - send) / 2} width={send} height={send} rx={1.5} className="demo-px demo-px-1" />
        {[0.75, 0.55, 0.65].map((f, r) => (
          <rect key={r} x={pad} y={pad + field + inner * 0.12 + r * inner * 0.19} width={inner * f} height={inner * 0.09} rx={1} className="demo-px demo-px-0" />
        ))}
      </>
    );
  }
  if (kind === 'terminal') {
    // A prompt, a cursor, and the lines Claude Code writes.
    const line = inner / 6;
    return (
      <>
        <rect x={pad * 0.6} y={pad * 0.6} width={size - 1.2 * pad} height={size - 1.2 * pad} rx={3} className="demo-term" />
        <polyline points={points([[pad + line * 0.2, pad + line * 0.5], [pad + line, pad + line], [pad + line * 0.2, pad + line * 1.5]])} className="demo-term-caret" />
        <rect x={pad + line * 1.4} y={pad + line * 1.3} width={line * 0.9} height={line * 0.22} className="demo-px demo-px-1" />
        {[0.8, 0.6, 0.7].map((f, r) => (
          <rect key={r} x={pad + line * 0.2} y={pad + line * (2.4 + r * 1.1)} width={inner * f} height={line * 0.32} rx={1} className="demo-term-line" />
        ))}
      </>
    );
  }
  if (kind === 'canvas') {
    const cell = inner / PIXELS[0].length;
    const top = (size - cell * PIXELS.length) / 2;
    return (
      <>
        {PIXELS.flatMap((row, r) =>
          [...row].map((c, k) => (
            <rect key={`${r}-${k}`} x={pad + k * cell + 0.4} y={top + r * cell + 0.4} width={cell - 0.8} height={cell - 0.8} className={`demo-px demo-px-${c}`} />
          ))
        )}
      </>
    );
  }
  if (kind === 'poll') {
    const row = inner / 3;
    return (
      <>
        {[0.9, 0.55, 0.3].map((f, r) => (
          <g key={r}>
            <rect x={pad} y={pad + r * row + row * 0.18} width={inner} height={row * 0.64} rx={1.5} className="demo-px demo-px-0" />
            <rect x={pad} y={pad + r * row + row * 0.18} width={inner * f} height={row * 0.64} rx={1.5} className={`demo-px demo-px-${r ? 2 : 1}`} />
          </g>
        ))}
      </>
    );
  }
  const gap = inner * 0.06;
  const col = (inner - 2 * gap) / 3;
  const card = inner / 5;
  return (
    <>
      {[2, 1, 3].map((cards, c) => (
        <g key={c}>
          <rect x={pad + c * (col + gap)} y={pad} width={col} height={inner} rx={1.5} className="demo-px demo-px-0" />
          {Array.from({ length: cards }, (_, k) => (
            <rect key={k} x={pad + c * (col + gap) + 1.5} y={pad + 1.5 + k * (card + 1.5)} width={col - 3} height={card} rx={1} className={`demo-px demo-px-${c === 1 ? 1 : 2}`} />
          ))}
        </g>
      ))}
    </>
  );
}

/** A box like `Iso`, with what it is laid on its top face; the ones that
 *  are apps link to them. */
function TopBox({ cx, cy, s, h, kind, tone }: { cx: number; cy: number; s: number; h: number; kind: GlyphKind; tone?: 'cool' | 'hot' }) {
  const k = s * C30;
  const m = s / 2;
  // The flat square's top-left corner goes to the top face's back corner,
  // its edges along the face's two back edges.
  const onTop = `matrix(${(k / s).toFixed(4)} ${(m / s).toFixed(4)} ${(-k / s).toFixed(4)} ${(m / s).toFixed(4)} ${cx} ${cy - m})`;
  const box = (
    <>
      <Iso cx={cx} cy={cy} s={s} h={h} tone={tone} />
      <g transform={onTop}>
        <Glyph kind={kind} size={s} />
      </g>
    </>
  );
  if (kind === 'terminal') return <g>{box}</g>;
  const app = APPS[kind];
  return (
    <a href={app.url} target="_blank" rel="noopener" className="demo-app" aria-label={`${app.name}, live`}>
      <title>{app.name}</title>
      {box}
    </a>
  );
}

/** A key: its bow centered on (x, y), the blade to the right. */
function Key({ x, y }: { x: number; y: number }) {
  return <path className="demo-key" d={`M ${x - 7} ${y} a 7 7 0 1 0 14 0 a 7 7 0 1 0 -14 0 M ${x + 7} ${y} h 20 M ${x + 22} ${y} v 5 M ${x + 26} ${y} v 4`} />;
}

const MARK_W = 52;
const MARK_H = Number(markHeight(MARK_W).toFixed(1));

/** Cloudish itself, keeping every key's credits. The mark is the site's own file (see Logo.tsx). */
function Platform({ x, y, w }: { x: number; y: number; w: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={84} rx={10} className="demo-card demo-card-hot" />
      <image href="/cloudish-mark.svg" x={x + 18} y={y + 42 - MARK_H / 2} width={MARK_W} height={MARK_H} />
      <text x={x + 86} y={y + 33} className="demo-t-brand">
        Cloudish
      </text>
      <text x={x + 86} y={y + 52} className="demo-t-body">
        one prepaid key funds every piece
      </text>
      <text x={x + 86} y={y + 70} className="demo-t-note demo-t-hot">
        nothing spends past its balance
      </text>
    </g>
  );
}

// ------------------------------------------- the system, left to right

function WideDrawing() {
  return (
    <svg viewBox="0 0 1080 520" className="demo-svg demo-svg-wide" role="group" aria-label={ALT}>
      {/* Claude, outside Cloudish. */}
      <rect x={840} y={36} width={220} height={52} rx={8} className="demo-card demo-card-cool" />
      <text x={858} y={58} className="demo-t-title">
        Claude
      </text>
      <text x={858} y={76} className="demo-t-note">
        the Anthropic API
      </text>

      {/* A visitor's prompt. */}
      <Browser x={16} y={190} />
      <Caption x={16} y={312} title="A visitor’s prompt" note="anyone, in a browser" />
      <Flow d="M 178 236 C 194 236 204 239 220 244" />

      {/* ① The public app, with ④ the model proxy on it. */}
      <TopBox cx={290} cy={215} s={84} h={56} tone="cool" kind="public" />
      <line x1={290} y1={110} x2={290} y2={173} className="demo-hair" />
      <rect x={230} y={70} width={128} height={40} rx={6} className="demo-card demo-card-cool" />
      <text x={246} y={87} className="demo-t-small">
        Model proxy
      </text>
      <text x={246} y={102} className="demo-t-note">
        a token per run
      </text>
      <Badge x={230} y={70} n={4} />
      <Caption x={234} y={342} badge={1} title="Public app" note="takes the prompts" />

      {/* ② The sandboxes: Claude Code in this run's, and the next prompt's to come. */}
      <rect x={500} y={120} width={260} height={272} rx={12} className="demo-region" />
      <Badge x={760} y={120} n={2} />
      <text x={744} y={146} textAnchor="end" className="demo-t-kicker">
        Sandboxes
      </text>
      <text x={744} y={163} textAnchor="end" className="demo-t-note">
        private, one per prompt
      </text>
      <TopBox cx={580} cy={228} s={76} h={50} tone="hot" kind="terminal" />
      <Caption x={532} y={342} title="Claude Code" hot note="with the app’s key" />
      <Iso cx={712} cy={316} s={34} h={24} tone="ghost" />
      <text x={712} y={378} textAnchor="middle" className="demo-t-note">
        the next one
      </text>
      <Flow d="M 366 232 C 410 222 470 224 516 238" />
      <text x={432} y={212} textAnchor="middle" className="demo-t-note">
        starts a sandbox
      </text>

      {/* The model's way to Claude: sandbox, proxy, Claude, over the top. */}
      <Flow tone="indigo" d="M 580 190 C 568 132 470 96 360 92" />
      <Flow tone="indigo" d="M 290 70 C 302 30 600 38 838 60" />

      {/* The apps, each on its own key. */}
      <TopBox cx={906} cy={196} s={56} h={38} kind="canvas" />
      <TopBox cx={1000} cy={190} s={56} h={38} kind="poll" />
      <TopBox cx={950} cy={246} s={70} h={46} kind="board" />
      <Caption x={867} y={342} title="Live apps" note="one per prompt, on HTTPS" />
      <Flow d="M 648 238 C 730 226 820 230 888 250" />
      <text x={772} y={268} className="demo-t-note">
        deploys with
      </text>
      <text x={772} y={282} className="demo-t-note">
        the app’s key
      </text>

      {/* Cloudish, and the credits: the public app and the sandboxes by the second, ③ each app's key once. */}
      <Platform x={420} y={424} w={320} />
      <Flow tone="money" d="M 420 462 C 356 462 300 426 294 384" />
      <text x={312} y={494} className="demo-t-note">
        by the second
      </text>
      <Flow tone="money" d="M 600 424 L 600 394" />
      <text x={612} y={414} className="demo-t-note">
        by the second
      </text>
      <Flow tone="money" d="M 740 466 L 921 466" />
      <Key x={930} y={466} />
      <Flow tone="money" d="M 950 458 L 950 384" />
      <Badge x={790} y={466} n={3} />
      <text x={808} y={490} className="demo-t-note">
        0.5 credits each, capped
      </text>
    </svg>
  );
}

// ---------------------------------------- the same system, top to bottom

function TallDrawing() {
  return (
    <svg viewBox="0 0 380 900" className="demo-svg demo-svg-tall" role="group" aria-label={ALT}>
      {/* Claude, and a visitor's prompt. */}
      <rect x={226} y={16} width={138} height={48} rx={8} className="demo-card demo-card-cool" />
      <text x={240} y={36} className="demo-t-title">
        Claude
      </text>
      <text x={240} y={53} className="demo-t-note">
        the Anthropic API
      </text>
      <text x={16} y={20} className="demo-t-kicker">
        A visitor’s prompt
      </text>
      <Browser x={16} y={30} />
      <Flow d="M 96 122 C 96 140 100 148 100 165" />

      {/* ① The public app, ④ the proxy beside it. */}
      <TopBox cx={100} cy={200} s={70} h={46} tone="cool" kind="public" />
      <Caption x={184} y={190} badge={1} title="Public app" note="takes the prompts" />
      <line x1={161} y1={236} x2={196} y2={255} className="demo-hair" />
      <rect x={196} y={236} width={124} height={40} rx={6} className="demo-card demo-card-cool" />
      <text x={210} y={253} className="demo-t-small">
        Model proxy
      </text>
      <text x={210} y={268} className="demo-t-note">
        a token per run
      </text>
      <Badge x={196} y={236} n={4} />
      <Flow tone="indigo" d="M 320 250 C 360 236 362 120 300 66" />

      {/* ② The sandboxes. */}
      <rect x={16} y={330} width={348} height={218} rx={12} className="demo-region" />
      <Badge x={364} y={330} n={2} />
      <text x={348} y={356} textAnchor="end" className="demo-t-kicker">
        Sandboxes
      </text>
      <text x={348} y={373} textAnchor="end" className="demo-t-note">
        private, one per prompt
      </text>
      <TopBox cx={100} cy={424} s={64} h={42} tone="hot" kind="terminal" />
      <Caption x={180} y={436} title="Claude Code" hot note="with the app’s key" />
      <Iso cx={300} cy={486} s={30} h={20} tone="ghost" />
      <text x={300} y={534} textAnchor="middle" className="demo-t-note">
        the next one
      </text>
      <Flow d="M 100 284 L 100 390" />
      <text x={112} y={316} className="demo-t-note">
        starts a sandbox
      </text>
      <Flow tone="indigo" d="M 130 404 C 160 360 240 330 258 278" />

      {/* The apps. */}
      <TopBox cx={72} cy={622} s={44} h={30} kind="canvas" />
      <TopBox cx={136} cy={618} s={44} h={30} kind="poll" />
      <TopBox cx={104} cy={658} s={56} h={38} kind="board" />
      <Caption x={180} y={648} title="Live apps" note="one per prompt, on HTTPS" />
      <Flow d="M 100 500 L 100 606" />
      <text x={112} y={570} className="demo-t-note">
        deploys with the app’s key
      </text>

      {/* Cloudish, and the credits. */}
      <Platform x={16} y={800} w={348} />
      <Flow tone="money" d="M 104 800 L 104 764" />
      <Key x={104} y={756} />
      <Flow tone="money" d="M 104 748 L 104 728" />
      <Badge x={160} y={756} n={3} />
      <text x={178} y={760} className="demo-t-note">
        0.5 credits each, capped
      </text>
      <Flow tone="money" d="M 16 830 C 6 830 6 820 6 800 L 6 250 C 6 240 20 238 40 240" />
      <Flow tone="money" d="M 6 450 L 44 450" />
      <text x={24} y={760} transform="rotate(-90 24 760)" className="demo-t-note">
        by the second
      </text>
    </svg>
  );
}

// ------------------------------------------------------------- section

function CopyPrompt() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PROMPT_TEXT);
    } catch {
      // Clipboard access can be blocked; the prompt is right there to select.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button type="button" className="btn btn-sm" onClick={copy}>
      {copied ? 'Prompt copied' : 'Copy the prompt'}
    </button>
  );
}

/** The prompt's first sentence, and the rest behind a toggle. While it's
 *  closed, a click anywhere on it opens it, the › included, which turns
 *  down while open and closes it again. The toggle at the end is the
 *  control for keyboards and screen readers; the › only repeats it. */
function Prompt() {
  const [open, setOpen] = useState(false);
  const [first, ...rest] = PROMPT;
  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen((o) => !o);
  };
  return (
    <div className={open ? 'demo-prompt is-open' : 'demo-prompt'} onClick={open ? undefined : () => setOpen(true)}>
      <button type="button" className="demo-prompt-caret" tabIndex={-1} aria-hidden="true" onClick={toggle}>
        ›
      </button>
      <p>
        <span className="demo-prompt-lead">{first.text}</span>
        <span id="demo-prompt-rest" className="demo-prompt-rest" hidden={!open}>
          {rest.map((p, i) => (
            <Fragment key={i}>
              {' '}
              <span className={p.deploy ? 'demo-prompt-deploy' : undefined}>
                {p.part ? (
                  <span className="demo-num" aria-hidden="true">
                    {p.part}
                  </span>
                ) : null}
                {p.text}
              </span>
            </Fragment>
          ))}
        </span>{' '}
        <button type="button" className="demo-prompt-toggle" aria-expanded={open} aria-controls="demo-prompt-rest" onClick={toggle}>
          {open ? 'Show less' : 'Show the whole prompt'}
        </button>
      </p>
    </div>
  );
}

export function DemoSection() {
  return (
    <section className="section" id="demo">
      <div className="section-wide">
        <div className="section-header">
          <span className="eyebrow">Demo</span>
          <h2>One prompt builds and deploys a system that builds apps</h2>
          <p className="muted">
            Plenty of tools turn a prompt into an app. Cloudish lets an agent like Claude Code build and deploy the tool
            itself from a single prompt, with no one stepping in: a service where anyone’s prompt gets its own Claude
            Code, in its own sandbox, on its own capped budget, and comes back a live app. That’s just one of the systems
            your agent could build this way.
          </p>
        </div>

        <div className="demo-panel">
          <div className="demo-panel-head">
            <span className="demo-kicker">The prompt behind the demo</span>
            <CopyPrompt />
          </div>
          <Prompt />
          <div className="demo-panel-head demo-panel-mid">
            <p>
              That’s about all it takes: give it to Claude Code with a Cloudish key and you get the bones of the system
              below. The rest of the demo is presentation.
            </p>
            <a className="demo-live" href={DEMO_URL}>
              <span className="dc-dot dc-dot-live" aria-hidden="true" />
              demo.cloudish.app
            </a>
          </div>
          <div className="demo-stage">
            <WideDrawing />
            <TallDrawing />
          </div>
        </div>

        <ol className="demo-parts">
          {PARTS.map((p) => (
            <li key={p.title}>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </li>
          ))}
        </ol>

        <div className="demo-cta">
          <a className="btn btn-primary btn-lg" href={DEMO_URL}>
            See the demo live
          </a>
          <a className="btn btn-lg" href={`${DEMO_URL}#system`}>
            How it’s built
          </a>
        </div>
        <p className="demo-cta-note">
          Real keys, real credits, real containers. Pick an app, watch the run that built it, every command and API call
          shown, and open the app it shipped.
        </p>
      </div>
    </section>
  );
}
